import { useCallback, useState } from 'react';
import { MAX_CHAT_MESSAGE_LENGTH } from '@wundo/shared/chat.ts';
import { formatFileSize } from '@wundo/shared/fileSize.ts';
import {
  MAX_ATTACHMENTS_PER_MESSAGE,
  MAX_FILE_BYTES,
  MAX_IMAGE_BYTES,
} from '@wundo/shared/attachments.ts';
import { m } from '@/paraglide/messages.js';
import { getLocale } from '@/paraglide/runtime.js';
import type { ChatAttachmentProblem } from './chatAttachments.ts';
import type { DictationProblem } from './useDictation.ts';

export interface ChatProblemOptions {
  conversationId: string | null;
  attachmentProblem: ChatAttachmentProblem | null;
  historyFailed: boolean;
}

/*
 * Kept apart from a failed send: nothing was attempted for a too long message,
 * so retrying in a moment is not the advice — shortening it is.
 */
interface SendProblem {
  kind: 'failed' | 'too_long';
  sitting: string;
}

export interface ChatProblem {
  /** What went wrong, if anything, in words the user can act on. */
  text: string | null;
  /** A message did not reach the conversation on screen. */
  sendFailed: () => void;
  /**
   * Whether a written message is short enough to send, saying so under the
   * conversation on screen when it is not.
   */
  admit: (text: string) => boolean;
  /** A message is on its way, or the drawer stands on a new conversation. */
  clearSend: () => void;
  setDictationProblem: (problem: DictationProblem | null) => void;
}

/**
 * The one line under the conversation that says why the last thing the user
 * tried to say did not arrive.
 *
 * A failed or too long message is about the sitting it was meant for, so the
 * warning must not be left standing under whichever one the user moves to
 * next. A sitting that does not exist yet is named the way the rest of the
 * drawer names it.
 */
export function useChatProblem({
  conversationId,
  attachmentProblem,
  historyFailed,
}: ChatProblemOptions): ChatProblem {
  const sitting = conversationId ?? 'new';
  /*
   * One warning about the last message and the sitting it was meant for: a new
   * attempt replaces it, so an older one cannot come back under a later message.
   */
  const [sendProblem, setSendProblem] = useState<SendProblem | null>(null);
  /*
   * Why the last thing said out loud never became a message. It stands in the
   * same place a refused file does, because it is the same kind of news.
   */
  const [dictationProblem, setDictationProblem] = useState<DictationProblem | null>(null);

  /*
   * A pasted verslag can be longer than one message may carry. Refused here
   * rather than by the server, so the drawer can say what is wrong instead of
   * calling it a send that did not go through.
   */
  const admit = useCallback(
    (text: string) => {
      const tooLong = text.trim().length > MAX_CHAT_MESSAGE_LENGTH;
      if (tooLong) setSendProblem({ kind: 'too_long', sitting });
      return !tooLong;
    },
    [sitting],
  );
  const sendFailed = useCallback(
    () => setSendProblem({ kind: 'failed', sitting }),
    [sitting],
  );
  const clearSend = useCallback(() => setSendProblem(null), []);
  const here = sendProblem?.sitting === sitting ? sendProblem.kind : null;

  return {
    text: problemText({
      dictationProblem,
      attachmentProblem,
      tooLong: here === 'too_long',
      sendFailed: here === 'failed',
      historyFailed,
    }),
    sendFailed,
    admit,
    clearSend,
    setDictationProblem,
  };
}

function problemText(problems: {
  dictationProblem: DictationProblem | null;
  attachmentProblem: ChatAttachmentProblem | null;
  tooLong: boolean;
  sendFailed: boolean;
  historyFailed: boolean;
}): string | null {
  if (problems.dictationProblem) return dictationProblemText(problems.dictationProblem);
  if (problems.attachmentProblem) {
    return attachmentProblemText(problems.attachmentProblem);
  }
  if (problems.tooLong) {
    return m.chat_too_long({
      count: MAX_CHAT_MESSAGE_LENGTH.toLocaleString(getLocale()),
    });
  }
  if (problems.sendFailed) return m.chat_send_failed();
  if (problems.historyFailed) return m.chat_load_failed();
  return null;
}

/**
 * Why a file was not taken, said in a way the user can do something about. The
 * limits come from the constants the backend enforces, so the sentence cannot
 * end up promising a cap nobody keeps.
 */
function attachmentProblemText(problem: ChatAttachmentProblem): string {
  switch (problem) {
    case 'type':
      return m.chat_attachment_type();
    case 'size':
      return m.chat_attachment_size({
        file_size: formatFileSize(MAX_FILE_BYTES),
        image_size: formatFileSize(MAX_IMAGE_BYTES),
      });
    case 'too_many':
      return m.chat_attachment_too_many({ count: MAX_ATTACHMENTS_PER_MESSAGE });
    case 'unreadable':
      return m.chat_attachment_unreadable();
    case 'failed':
      return m.chat_attachment_failed();
  }
}

/**
 * Why nothing was said out loud in the end, with what to do about it: give
 * Wundo the microphone, say it again, or fall back to typing.
 */
function dictationProblemText(problem: DictationProblem): string {
  switch (problem) {
    case 'blocked':
      return m.chat_dictate_blocked();
    case 'empty':
      return m.chat_dictate_empty();
    case 'failed':
      return m.chat_dictate_failed();
  }
}
